import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion as Motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { smoothScrollIntoView } from '../../utils/smoothScroll';
import {
  ArrowRight,
  Plus,
  Minus,
  Check,
  Code2,
  GitBranch,
  Compass,
  Presentation,
  FlaskConical,
  Users,
  Rocket,
  Zap,
  Route,
  Target,
  Gauge,
  Timer,
  Layers,
  MonitorSmartphone,
  FileCode,
  ShieldCheck,
  Boxes,
} from 'lucide-react';
import TeaserVideo from './components/TeaserVideo';
import SectionHeading from './components/SectionHeading';
import ProcessPinnedSection from '../ProcessPinnedSection';
import PrototypeConfigurator, { BASE_PRICE, BASE_DAYS } from './components/PrototypeConfigurator';
import Cre8AppsGallery from './components/Cre8AppsGallery';
import { StatsStrip } from '../../components/Stats';
import {
  HeroMarqueeTrack,
  GridOverlay,
  CropMarks,
  Crosshair,
  FlowChain,
  SystemMarker,
  SectionCounter,
  VerticalLabel,
  SectionFrame,
} from '../../components/swiss/SwissKit';

const ORIGIN = 'https://cre8now.design';
// Teaser reel; set to null to hide the teaser section.
const PROTOTYPING_VIDEO = '/assets/prototyping-teaser.mp4';
const TOTAL_SECTIONS = 8;

/* ------------------------------------------------------------------ atoms */

const FaqItem = React.memo(({ item, idx, color }) => {
  const [open, setOpen] = useState(false);
  return (
    <Motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: Math.min(idx, 6) * 0.05 }}
      viewport={{ once: false, amount: 0.2 }}
      className="border-t border-border-color py-6 md:py-8"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-start justify-between gap-6 cursor-pointer text-left"
      >
        <div className="flex items-start gap-4 md:gap-8 flex-1">
          <span className="font-mono-swiss text-[10px] tracking-[0.2em] opacity-40 pt-1.5 flex-shrink-0 tabular-nums">
            {String(idx + 1).padStart(2, '0')}
          </span>
          <h5 className="text-lg md:text-2xl font-black tracking-tight pr-4">{item.q}</h5>
        </div>
        <Motion.span
          className="flex-shrink-0 mt-1"
          style={{ color }}
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.35, ease: [0.83, 0, 0.17, 1] }}
        >
          {open ? <Minus size={18} /> : <Plus size={18} />}
        </Motion.span>
      </button>
      {/* Answer stays mounted so it is crawlable even while collapsed. */}
      <Motion.div
        initial={false}
        animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.83, 0, 0.17, 1] }}
        className="overflow-hidden"
      >
        <p className="mt-4 md:mt-5 md:ml-[52px] text-text-muted font-light leading-relaxed max-w-[1100px] pb-1">{item.a}</p>
      </Motion.div>
    </Motion.div>
  );
});

/* One action per section, deliberately. Every CTA on this page points at the
   configurator: the scope call is what the configurator hands off to once a
   scope exists, so offering both side by side asked the visitor to choose
   between a step and the step after it. */
const SectionCTA = ({ color, label, onClick }) => (
  <div className="w-full flex flex-wrap justify-center items-center gap-4 py-16 md:py-24 border-t border-border-color">
    <Motion.button
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="group relative px-8 py-4 md:px-12 md:py-5 font-black uppercase ui-text transition-all duration-500 overflow-hidden bg-text-color text-bg-color rounded-[4px]"
    >
      <div
        className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500"
        style={{ backgroundColor: color }}
      />
      <span className="relative z-10 flex items-center transition-colors duration-500 group-hover:text-white">
        {label}
        <span className="w-0 opacity-0 group-hover:w-5 group-hover:ml-3 group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
          <ArrowRight size={18} />
        </span>
      </span>
    </Motion.button>
  </div>
);

/* Pain and win rows share one shape: a numbered hairline row with a heading
   and a supporting line. Deliberately not cards, the whole page reads as one
   editorial column rather than a SaaS dashboard. */
const LedgerRow = ({ item, idx, color, tone = 'default' }) => {
  const Icon = item.icon;
  return (
  <Motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: Math.min(idx, 5) * 0.06, ease: [0.16, 1, 0.3, 1] }}
    viewport={{ once: true, amount: 0.3 }}
    className="group border-t border-border-color py-8 md:py-10 flex items-start gap-5 md:gap-10"
  >
    <span className="font-mono-swiss text-[10px] tracking-[0.2em] opacity-35 pt-2 flex-shrink-0 tabular-nums">
      {String(idx + 1).padStart(2, '0')}
    </span>
    <span
      className="flex-shrink-0 mt-1 transition-transform duration-500 group-hover:-translate-y-0.5"
      style={{ color: tone === 'win' ? color : 'currentColor', opacity: tone === 'win' ? 1 : 0.4 }}
    >
      <Icon size={20} strokeWidth={1.6} />
    </span>
    <div className="flex-1">
      <h4 className="font-black leading-none mb-5 tracking-tighter text-text-color">
        {item.title}
      </h4>
      <p className="text-text-muted leading-relaxed font-light max-w-[1100px]">{item.desc}</p>
    </div>
  </Motion.div>
  );
};

/* Same audience tile the Concierge page uses for its industry grid: identical
   padding, nth-child border rules, circular accent icon, oversized parallax
   watermark, h4 title and tag pills. Kept as its own copy here rather than
   imported so the Concierge file stays untouched. */
const GoalCard = React.memo(({ item, i, color, theme }) => {
  const Icon = item.icon;
  const isLight = theme === 'light';
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const iconY = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <Motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
      viewport={{ once: true, amount: 0.1 }}
      className="group p-14 md:p-20 md:pt-14 md:pb-16 border-b border-border-color md:border-r md:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(3n-1)]:border-r lg:[&:nth-child(3n-2)]:border-r lg:[&:nth-child(3n)]:border-r-0 bg-surface-color/30 flex flex-col justify-between relative overflow-hidden gpu-accelerated"
    >
      <Motion.div
        style={{ y: iconY, translateZ: 0 }}
        className="absolute top-0 right-0 p-10 opacity-[0.02] pointer-events-none block"
      >
        <Icon size={300} strokeWidth={1} color={isLight ? '#000000' : '#ffffff'} />
      </Motion.div>

      <div className="relative z-10">
        <div className="flex justify-between items-start mb-10">
          <div
            className="w-24 h-24 md:w-20 md:h-20 rounded-full flex items-center justify-center bg-surface-color"
            style={{ color: color }}
          >
            <Icon size={40} strokeWidth={1.5} />
          </div>
        </div>

        <h4 className="font-black leading-none mb-5 tracking-tighter text-text-color">
          {item.title}
        </h4>
        <p className="text-text-muted leading-relaxed font-light mb-8">
          {item.desc}
        </p>

        {item.tags && (
          <div className="flex flex-wrap gap-2 mt-auto">
            {item.tags.map((tag, idx) => (
              <span
                key={idx}
                className="text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-1.5 border border-border-color rounded-[4px] text-text-muted"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </Motion.div>
  );
});

/* ----------------------------------------------------------------- content */

const CONTENT = {
  de: {
    seoTitle: 'Rapid Prototyping Agentur Berlin | Coded Product Prototypes in 7 Tagen | CRE8NOW',
    seoDescription:
      'CRE8NOW baut coded Product Prototypes in 7 Werktagen: klickbar im Browser, echter React-Code, Git-Repo inklusive. Festpreis ab 2.500 Euro, Scope live konfigurierbar. 25+ Jahre Erfahrung, 450+ Projekte.',
    serviceName: 'Rapid Prototyping',
    hero: {
      kicker: 'Coded Product Prototypes für',
      // Kept to roughly Concierge's word length: the H1 reserves its width
      // from the longest entry, and anything longer blows out the hero.
      words: ['Validierung', 'Pitches', 'Entscheidungen', 'Alignment', 'Nutzertests', 'MVP-Starts', 'AI-Ideen', 'Relaunches'],
      description:
        'Rapid Prototyping bei CRE8NOW heißt: in 7 Werktagen ein klickbarer Produkt-Prototyp im Browser, echter Code, echte Zustände, echtes Nutzer-Testing. Kein Wegwerf-Klickdummy, sondern eine saubere Codebasis mit Git-Repo, auf der dein Entwicklungsteam direkt weiterbaut.',
      cta: 'Projekt konfigurieren',
      marquee: [
        'Coded Prototype im Browser',
        'Ab 2.500 Euro Festpreis',
        '70 % vor Start, 30 % vor Übergabe',
        '7 Werktage Lieferzeit',
        'Vollständiger Quellcode',
        'Git-Repository inklusive',
        'Responsive UI',
        'Wiederverwendbare Komponenten',
        'Live-Preview per Link',
        'Bereit für Nutzertests',
      ],
    },
    teaser: {
      label: 'Coded Prototype Reel',
      title: 'Sieh dir an, wie ein coded Prototype läuft',
    },
    proof: [
      { value: '25+', label: 'Jahre Produkterfahrung' },
      { value: '450+', label: 'umgesetzte Projekte' },
      { value: '60+', label: 'internationale Kunden' },
      { value: '7', label: 'Werktage bis zum Prototyp' },
    ],
    pains: {
      label: 'Der Status quo',
      title: 'Wo Produktideen scheitern',
      highlight: 'Ohne Prototyp',
      description:
        'Zwischen Idee und Entwicklungsbudget liegt eine Lücke, in der die meisten digitalen Produkte stecken bleiben. Das sind die Reibungspunkte, die CRE8NOW in Discovery-Phasen immer wieder sieht.',
      items: [
        {
          icon: Presentation,
          title: 'Das Deck überzeugt niemanden mehr',
          desc: 'Slides beweisen nichts. Investoren, Gremien und Kunden haben hunderte Präsentationen gesehen und wollen klicken statt zuhören. Ohne erlebbares Produkt bleibt deine Idee eine Behauptung.',
        },
        {
          icon: MonitorSmartphone,
          title: 'Klickdummies brechen beim ersten echten Nutzer',
          desc: 'Verlinkte Screens halten genau einen vorgezeichneten Pfad aus. Sobald jemand abbiegt, etwas Falsches eingibt oder zurückgeht, endet die Demo. Genau dort beginnen die Erkenntnisse, die du brauchst.',
        },
        {
          icon: Users,
          title: 'Das Team diskutiert seit Wochen im Kreis',
          desc: 'Ohne funktionierendes Artefakt diskutieren Product, Design und Engineering Meinungen statt Verhalten. Jedes Meeting startet die Grundsatzdebatte neu und die Roadmap steht still.',
        },
        {
          icon: Code2,
          title: 'Engineering baut ins Blaue',
          desc: 'Anforderungen ohne erprobten Flow landen ungetestet im Sprint. Edge Cases, Zustände und Datenlogik tauchen erst in der Implementierung auf und werden dort um ein Vielfaches teurer.',
        },
        {
          icon: Gauge,
          title: 'Die Budget-Freigabe hängt am Beweis',
          desc: 'Niemand gibt ein sechsstelliges Entwicklungsbudget frei, solange das Produkt nur auf Papier existiert. Der Business Case braucht etwas, das man vorführen und messen kann.',
        },
        {
          icon: Boxes,
          title: 'Der Prototyp landet im Papierkorb',
          desc: 'Wegwerf-Prototypen verbrennen Wochen und hinterlassen nichts. Was fehlt, ist ein Artefakt, das gleichzeitig als Beweis dient und als Startpunkt für die echte Entwicklung taugt.',
        },
      ],
    },
    wins: {
      label: 'Das Ergebnis',
      title: 'Was nach 7 Werktagen dasteht',
      highlight: 'Spüre dein Produkt',
      description:
        'Ein coded Product Prototype ist kein Bild eines Produkts, sondern ein funktionierendes Stück Software. Das bekommst du in jedem Scope, unabhängig davon, wie du den Konfigurator einstellst.',
      items: [
        {
          icon: MonitorSmartphone,
          title: 'Ein klickbarer Prototyp im Browser',
          desc: 'Echte Screens, echte Zustände, echte Interaktion, responsiv auf Desktop und Mobile. Teilbar per Link, ohne Installation, ohne Erklärvideo. Wer den Link öffnet, versteht das Produkt in zwei Minuten.',
        },
        {
          icon: FileCode,
          title: 'Echter Code statt Wegwerf-Artefakt',
          desc: 'Sauber strukturierte Komponenten, lesbar dokumentiert und mit vollständigem Quellcode im Git-Repository. Dein Entwicklungsteam übernimmt die Codebasis und baut weiter, statt bei null anzufangen.',
        },
        {
          icon: FlaskConical,
          title: 'Entscheidungen auf Basis von Verhalten',
          desc: 'Mit einem funktionierenden Prototyp testest du mit echten Nutzern unter realistischen Bedingungen. Du siehst, wo Menschen abbrechen, zögern oder etwas anderes tun als erwartet.',
        },
        {
          icon: Presentation,
          title: 'Ein Pitch, der klickt',
          desc: 'Investoren, Gremien und Kunden erleben das Produkt, statt es sich vorzustellen. Das verkürzt Entscheidungswege und verschiebt das Gespräch von "ob" zu "wann".',
        },
        {
          icon: Layers,
          title: 'Klarheit für das Engineering',
          desc: 'Flows, Edge Cases, Zustände und Komponentenstruktur sind vorab durchgespielt. Der anschließende Build startet mit erprobten Entscheidungen statt mit offenen Fragen.',
        },
        {
          icon: Timer,
          title: 'Planbarkeit ab Tag null',
          desc: `Festpreis ab ${BASE_PRICE.toLocaleString('de-DE')} Euro und ${BASE_DAYS} Werktage Basis-Lieferzeit. Jede Scope-Erweiterung ist transparent bepreist, bevor das Projekt startet. Keine Stundenzettel, keine Überraschungen.`,
        },
      ],
    },
    goals: {
      label: 'Für welches Ziel',
      title: 'Rapid Prototyping lohnt sich für',
      highlight: 'Dein Ziel',
      description:
        'Nicht die Branche entscheidet, ob Rapid Prototyping sinnvoll ist, sondern das Ziel dahinter. Such dir das Ziel, das gerade auf deinem Tisch liegt.',
      items: [
        {
          icon: Compass,
          title: 'Du willst validieren, bevor Budget fließt',
          desc: 'Ein Prototyp kostet einen Bruchteil der Entwicklung und beantwortet die teuerste Frage zuerst: Löst dieses Produkt überhaupt ein echtes Problem?',
          tags: ['Produktvalidierung', 'Proof of Concept', 'Risiko senken', 'Business Case'],
        },
        {
          icon: Presentation,
          title: 'Du willst Investoren und Gremien überzeugen',
          desc: 'Eine funktionierende Demo schlägt jedes Deck. Zeig dein Produkt live im Browser, statt es über zwanzig Slides zu erklären.',
          tags: ['Investoren-Pitch', 'Funding-Runde', 'Stakeholder-Buy-in', 'Demo-Link'],
        },
        {
          icon: FlaskConical,
          title: 'Du willst mit echten Nutzern testen',
          desc: 'Usability-Tests auf einem klickbaren Produkt liefern belastbare Ergebnisse. Nutzer verhalten sich anders, sobald das Interface wirklich reagiert.',
          tags: ['Usability-Test', 'User Research', 'Conversion-Hypothesen', 'A/B-Varianten'],
        },
        {
          icon: Users,
          title: 'Du willst dein Team auf eine Richtung einnorden',
          desc: 'Ein gemeinsamer, klickbarer Stand beendet Grundsatzdiskussionen. Alle sehen dasselbe Produkt statt sechs unterschiedliche Vorstellungen davon.',
          tags: ['Team-Alignment', 'Product Discovery', 'Workshop-Grundlage', 'Roadmap'],
        },
        {
          icon: Code2,
          title: 'Du willst dem Engineering Vorarbeit liefern',
          desc: 'Erprobte Flows, definierte Zustände und eine saubere Komponentenstruktur sparen im anschließenden Build ganze Sprints.',
          tags: ['Technische Vorarbeit', 'Komponentenstruktur', 'Edge Cases', 'Git-Repo'],
        },
        {
          icon: Rocket,
          title: 'Du willst schneller am Markt sein',
          desc: 'Während andere noch spezifizieren, testest du bereits. Rapid Prototyping verkürzt die Strecke zwischen Idee und erstem echten Feedback auf Tage.',
          tags: ['Time-to-Market', 'MVP', 'Schneller Launch', 'Wettbewerbsvorsprung'],
        },
        {
          icon: Zap,
          title: 'Du willst eine AI-Idee greifbar machen',
          desc: 'AI-Features überzeugen erst, wenn man sie benutzt. CRE8NOW bindet LLMs und AI-Workflows direkt in den Prototyp ein, statt sie zu simulieren.',
          tags: ['AI Prototyping', 'LLM-Integration', 'AI-Workflows', 'Machbarkeit'],
        },
        {
          icon: Route,
          title: 'Du willst ein bestehendes Produkt neu denken',
          desc: 'Ein Relaunch-Konzept wird angreifbar, sobald man es bedienen kann. Teste die neue Struktur, bevor du sie auf eine laufende Plattform loslässt.',
          tags: ['Relaunch', 'Redesign-Test', 'Informationsarchitektur', 'Migrationsrisiko'],
        },
        {
          icon: Target,
          title: 'Du willst eine Feature-Entscheidung absichern',
          desc: 'Bevor ein großes Feature in die Roadmap wandert, lohnt der Test am funktionierenden Ausschnitt. Ein Flow, gebaut und geprüft, klärt mehr als jede Schätzrunde.',
          tags: ['Feature-Validierung', 'Roadmap-Entscheidung', 'Scope-Schärfung', 'Priorisierung'],
        },
      ],
    },
    process: {
      label: 'Der Ablauf',
      title: 'So entsteht dein Prototyp',
      highlight: 'In 7 Tagen',
      description:
        'Ein fester Ablauf über sieben Werktage Basis-Scope. Du bist an zwei Punkten eingebunden, den Rest übernimmt CRE8NOW.',
      steps: [
        {
          step: '01',
          title: 'Scope-Call',
          text: 'Du konfigurierst deinen Scope hier auf der Seite, CRE8NOW bestätigt ihn im Gespräch. Danach stehen Festpreis, Liefertermin und der genaue Funktionsumfang schriftlich fest.',
        },
        {
          step: '02',
          title: 'Flows & Interface',
          text: 'CRE8NOW schneidet den Prototyp auf die Frage zu, die er beantworten soll: zentrale Flows, Zustände, Screens. Bestehende Designsysteme werden übernommen, sonst entsteht eine eigene visuelle Richtung.',
        },
        {
          step: '03',
          title: 'Build',
          text: 'Umsetzung als echte Anwendung mit wiederverwendbaren Komponenten, responsivem Layout und, je nach Scope, Datenbank, Authentifizierung und Integrationen. Fortschritt ist jederzeit per Live-Preview sichtbar.',
        },
        {
          step: '04',
          title: 'Review & QA',
          text: 'Eine gemeinsame Review-Runde, dann Feinschliff und Qualitätssicherung über Browser und Geräte hinweg. Korrekturen aus dieser Runde sind im Festpreis enthalten.',
        },
        {
          step: '05',
          title: 'Übergabe',
          text: 'Du bekommst Live-Preview, vollständigen Quellcode, Zugriff auf das Git-Repository und eine Basis-Dokumentation. Dein Team kann am selben Tag weiterbauen.',
        },
      ],
    },
    configurator: {
      label: 'Scope & Preis',
      title: 'Konfiguriere deinen Scope',
      highlight: 'Festpreis',
      description: `Neun Fragen, ein transparenter Preis. Der Basis-Scope startet bei ${BASE_PRICE.toLocaleString('de-DE')} Euro und ${BASE_DAYS} Werktagen. Jede Antwort addiert entweder nichts oder einen klar ausgewiesenen Betrag und zusätzliche Werktage. Nichts wird versteckt, nichts wird abgezogen. Bezahlt wird in zwei Raten: 70 % vor Projektstart, 30 % vor der Datenübergabe.`,
    },
    faq: {
      label: 'FAQ',
      title: 'Häufige',
      highlight: 'Fragen',
      items: [
        {
          q: 'Was genau ist ein coded Prototype und wie unterscheidet er sich von einem Figma-Prototyp?',
          a: 'Ein Figma-Prototyp verlinkt Bilder von Screens. Ein coded Prototype ist echte Software im Browser: Eingaben werden verarbeitet, Zustände ändern sich, Daten werden gespeichert, Fehlerfälle greifen. Dadurch hält er auch dann stand, wenn ein Nutzer vom gedachten Pfad abweicht, und genau dort entstehen die Erkenntnisse, für die du testest.',
        },
        {
          q: 'Bekomme ich den Quellcode wirklich?',
          a: 'Ja, vollständig und ohne Zusatzkosten. Zur Übergabe gehören der komplette Quellcode, Zugriff auf das Git-Repository mit der gesamten Historie und eine Basis-Dokumentation. Es gibt keine Lizenzbindung und keinen Lock-in an CRE8NOW.',
        },
        {
          q: 'Kann mein Entwicklungsteam auf dem Prototyp weiterbauen?',
          a: 'Genau dafür ist er gebaut. CRE8NOW arbeitet mit wiederverwendbaren Komponenten, klarer Ordnerstruktur und gängigen Konventionen, damit der Prototyp als Startpunkt für die Produktentwicklung taugt. Was im Prototyp bewusst vereinfacht ist, etwa Mock-Daten, ist in der Dokumentation markiert.',
        },
        {
          q: 'Was passiert, wenn mein Vorhaben größer ist, als der Konfigurator abbildet?',
          a: 'Dann konfigurierst du den nächstliegenden Scope und beschreibst den Rest im Formular. Der Konfigurator deckt den Großteil typischer Prototyp-Vorhaben ab. Für alles darüber hinaus erstellt CRE8NOW nach dem Scope-Call ein individuelles Festpreisangebot auf derselben Logik.',
        },
        {
          q: 'Wie verbindlich sind Preis und Liefertermin?',
          a: 'Die Zahlen im Konfigurator sind eine erste Schätzung auf Basis deiner Angaben. Im Scope-Call wird der Umfang geschärft, danach bestätigt CRE8NOW Festpreis und Liefertermin schriftlich. Ab diesem Punkt sind beide verbindlich. Abgerechnet wird nicht nach Stunden.',
        },
        {
          q: 'Wie läuft die Bezahlung?',
          a: 'In zwei Raten: 70 % des Festpreises sind vor Projektstart fällig, die restlichen 30 % vor der Datenübergabe. Die erste Rate sichert deinen Slot im Kalender, die zweite wird erst fällig, wenn der Prototyp fertig ist und du ihn gesehen hast. Erst danach gehen Quellcode, Git-Zugriff und Dokumentation an dich über. Es gibt keine Stundenabrechnung und keine Nachforderungen, solange der Scope unverändert bleibt.',
        },
        {
          q: 'Welcher Technologie-Stack kommt zum Einsatz?',
          a: 'Standard ist ein modernes React-Setup mit Vite und Tailwind, bei Bedarf ergänzt um eine Datenbank, Authentifizierung und Anbindungen an externe APIs oder LLMs. Wenn dein Team bereits auf einem bestimmten Stack arbeitet, richtet sich CRE8NOW im Rahmen des Sinnvollen danach.',
        },
        {
          q: 'Wie läuft das mit Daten und Datenschutz?',
          a: 'Prototypen laufen standardmäßig mit Mock-Daten, echte Kundendaten sind für Tests selten nötig. Wird Persistenz gebraucht, setzt CRE8NOW auf EU-Hosting und DSGVO-konforme Dienste. Auf Wunsch wird vor Projektstart eine Vertraulichkeitsvereinbarung unterzeichnet.',
        },
        {
          q: 'Was braucht CRE8NOW von dir, damit sieben Werktage reichen?',
          a: 'Eine klare Vorstellung davon, welche Frage der Prototyp beantworten soll, eine feste Ansprechperson mit Entscheidungsbefugnis und, falls vorhanden, Zugang zu Marke, Designsystem und relevanten Schnittstellen. Fehlt davon etwas, bildet der Konzeptaufschlag im Konfigurator genau diesen Mehraufwand ab.',
        },
      ],
    },
    related: {
      label: 'Weiterlesen',
      title: 'Passende Leistungen',
      items: [
        { href: '/de/design/prototyping', title: 'Prototyping', desc: 'Die Leistungsseite zu Prototyping als UX-Methode: klickbare Wireframes, frühes Nutzerfeedback, validierte Konzepte.' },
        { href: '/de/design/ux-design', title: 'UX Design', desc: 'Nutzerzentrierte Produktkonzeption, Research und Flows als Grundlage für jeden belastbaren Prototyp.' },
        { href: '/de/design/development', title: 'Development', desc: 'Wenn aus dem Prototyp ein Produkt wird: Umsetzung in Webflow, Framer oder eigenem Code.' },
        { href: '/de/ai/custom-ai-development', title: 'Custom AI Development', desc: 'Individuelle AI-Lösungen und LLM-Integrationen, die im Prototyp bereits echt funktionieren.' },
      ],
    },
    finalCta: {
      title: 'Bereit für einen Prototyp, der klickt?',
      desc: 'Stell deinen Scope in neun Fragen zusammen und erhalte sofort Preis und Liefertermin. Unverbindlich, ohne Registrierung.',
      primary: 'Projekt konfigurieren',
    },
  },

  en: {
    seoTitle: 'Rapid Prototyping Agency Berlin | Coded Product Prototypes in 7 Days | CRE8NOW',
    seoDescription:
      'CRE8NOW builds coded product prototypes in 7 business days: clickable in the browser, real React code, Git repository included. Fixed price from €2,500, scope configurable live. 25+ years of experience, 450+ projects.',
    serviceName: 'Rapid Prototyping',
    hero: {
      kicker: 'Coded product prototypes for',
      // Kept to roughly Concierge's word length: the H1 reserves its width
      // from the longest entry, and anything longer blows out the hero.
      words: ['Validation', 'Pitches', 'Decisions', 'Alignment', 'User Testing', 'MVP Launches', 'AI Ideas', 'Relaunches'],
      description:
        'Rapid prototyping at CRE8NOW means: a clickable product prototype in the browser in 7 business days, real code, real states, real user testing. Not a throwaway click dummy but a clean codebase with a Git repository your engineering team continues to build on.',
      cta: 'Configure your project',
      marquee: [
        'Coded browser prototype',
        'Fixed price from €2,500',
        '70% before start, 30% before handover',
        '7 business days delivery',
        'Complete source code',
        'Git repository included',
        'Responsive UI',
        'Reusable components',
        'Live preview by link',
        'Ready for user testing',
      ],
    },
    teaser: {
      label: 'Coded Prototype Reel',
      title: 'See a coded prototype in motion',
    },
    proof: [
      { value: '25+', label: 'years of product experience' },
      { value: '450+', label: 'projects delivered' },
      { value: '60+', label: 'international clients' },
      { value: '7', label: 'business days to prototype' },
    ],
    pains: {
      label: 'The status quo',
      title: 'Where product ideas stall',
      highlight: 'Without Proof',
      description:
        'Between the idea and the development budget sits a gap where most digital products get stuck. These are the friction points CRE8NOW keeps running into during discovery.',
      items: [
        {
          icon: Presentation,
          title: 'The deck convinces nobody anymore',
          desc: 'Slides prove nothing. Investors, boards and clients have seen hundreds of decks and would rather click than listen. Without a product they can experience, your idea stays a claim.',
        },
        {
          icon: MonitorSmartphone,
          title: 'Click dummies break on the first real user',
          desc: 'Linked screens survive exactly one rehearsed path. The moment someone turns off it, types the wrong thing or goes back, the demo ends. That is precisely where the insight you need begins.',
        },
        {
          icon: Users,
          title: 'The team has been going in circles for weeks',
          desc: 'Without a working artefact, product, design and engineering argue about opinions instead of behaviour. Every meeting restarts the same debate and the roadmap stands still.',
        },
        {
          icon: Code2,
          title: 'Engineering is building blind',
          desc: 'Requirements without a tested flow land in the sprint unvalidated. Edge cases, states and data logic only surface during implementation, where fixing them costs a multiple.',
        },
        {
          icon: Gauge,
          title: 'Budget approval is waiting on proof',
          desc: 'Nobody signs off a six-figure development budget while the product exists only on paper. The business case needs something that can be demonstrated and measured.',
        },
        {
          icon: Boxes,
          title: 'The prototype ends up in the bin',
          desc: 'Throwaway prototypes burn weeks and leave nothing behind. What is missing is an artefact that works as proof and as the starting point for the real build at the same time.',
        },
      ],
    },
    wins: {
      label: 'The outcome',
      title: 'What you hold after 7 business days',
      highlight: 'Feel your product',
      description:
        'A coded product prototype is not a picture of a product, it is a working piece of software. You get all of this in every scope, no matter how you set the configurator.',
      items: [
        {
          icon: MonitorSmartphone,
          title: 'A clickable prototype in the browser',
          desc: 'Real screens, real states, real interaction, responsive on desktop and mobile. Shareable by link, no install, no explainer video. Whoever opens the link understands the product in two minutes.',
        },
        {
          icon: FileCode,
          title: 'Real code, not a throwaway artefact',
          desc: 'Cleanly structured components, readable documentation and the complete source code in a Git repository. Your engineering team takes over the codebase instead of starting from zero.',
        },
        {
          icon: FlaskConical,
          title: 'Decisions based on behaviour',
          desc: 'With a working prototype you test with real users under realistic conditions. You see where people drop off, hesitate or do something entirely different from what you expected.',
        },
        {
          icon: Presentation,
          title: 'A pitch that clicks',
          desc: 'Investors, boards and clients experience the product instead of imagining it. That shortens decision paths and moves the conversation from "whether" to "when".',
        },
        {
          icon: Layers,
          title: 'Clarity for engineering',
          desc: 'Flows, edge cases, states and component structure are worked through up front. The build that follows starts with tested decisions instead of open questions.',
        },
        {
          icon: Timer,
          title: 'Predictability from day zero',
          desc: `Fixed price from €${BASE_PRICE.toLocaleString('en-US')} and ${BASE_DAYS} business days of base delivery time. Every scope addition is priced transparently before the project starts. No timesheets, no surprises.`,
        },
      ],
    },
    goals: {
      label: 'Which goal',
      title: 'Rapid prototyping pays off for',
      highlight: 'Your Goal',
      description:
        'Your industry does not decide whether rapid prototyping makes sense, your goal does. Pick the one currently sitting on your desk.',
      items: [
        {
          icon: Compass,
          title: 'You want to validate before budget moves',
          desc: 'A prototype costs a fraction of development and answers the most expensive question first: does this product actually solve a real problem?',
          tags: ['Product validation', 'Proof of concept', 'De-risking', 'Business case'],
        },
        {
          icon: Presentation,
          title: 'You want to convince investors and boards',
          desc: 'A working demo beats any deck. Show your product live in the browser instead of explaining it across twenty slides.',
          tags: ['Investor pitch', 'Funding round', 'Stakeholder buy-in', 'Demo link'],
        },
        {
          icon: FlaskConical,
          title: 'You want to test with real users',
          desc: 'Usability testing on a clickable product produces results you can act on. People behave differently the moment the interface actually responds.',
          tags: ['Usability testing', 'User research', 'Conversion hypotheses', 'A/B variants'],
        },
        {
          icon: Users,
          title: 'You want to align your team on one direction',
          desc: 'A shared, clickable state ends the debate about fundamentals. Everyone sees the same product instead of six different mental versions of it.',
          tags: ['Team alignment', 'Product discovery', 'Workshop basis', 'Roadmap'],
        },
        {
          icon: Code2,
          title: 'You want to hand engineering a head start',
          desc: 'Tested flows, defined states and a clean component structure save entire sprints in the build that follows.',
          tags: ['Technical groundwork', 'Component structure', 'Edge cases', 'Git repo'],
        },
        {
          icon: Rocket,
          title: 'You want to reach the market sooner',
          desc: 'While others are still writing specs, you are already testing. Rapid prototyping cuts the distance between idea and first real feedback down to days.',
          tags: ['Time to market', 'MVP', 'Faster launch', 'Competitive edge'],
        },
        {
          icon: Zap,
          title: 'You want to make an AI idea tangible',
          desc: 'AI features only convince once people use them. CRE8NOW wires LLMs and AI workflows directly into the prototype instead of faking them.',
          tags: ['AI prototyping', 'LLM integration', 'AI workflows', 'Feasibility'],
        },
        {
          icon: Route,
          title: 'You want to rethink an existing product',
          desc: 'A relaunch concept becomes testable the moment you can operate it. Try the new structure before releasing it onto a live platform.',
          tags: ['Relaunch', 'Redesign test', 'Information architecture', 'Migration risk'],
        },
        {
          icon: Target,
          title: 'You want to de-risk a feature decision',
          desc: 'Before a major feature enters the roadmap, test it on a working slice. One flow, built and validated, settles more than any estimation round.',
          tags: ['Feature validation', 'Roadmap decision', 'Scope sharpening', 'Prioritisation'],
        },
      ],
    },
    process: {
      label: 'The process',
      title: 'How your prototype gets built',
      highlight: 'In 7 Days',
      description:
        'A fixed sequence across seven business days of base scope. You are involved at two points, CRE8NOW handles the rest.',
      steps: [
        {
          step: '01',
          title: 'Scope call',
          text: 'You configure your scope here on the page, CRE8NOW confirms it on a call. After that, fixed price, delivery date and the exact functional scope are agreed in writing.',
        },
        {
          step: '02',
          title: 'Flows & interface',
          text: 'CRE8NOW cuts the prototype down to the question it has to answer: key flows, states, screens. Existing design systems are adopted, otherwise a dedicated visual direction is created.',
        },
        {
          step: '03',
          title: 'Build',
          text: 'Implementation as a real application with reusable components, responsive layout and, depending on scope, database, authentication and integrations. Progress is visible at any time via live preview.',
        },
        {
          step: '04',
          title: 'Review & QA',
          text: 'One shared review round, then refinement and quality assurance across browsers and devices. Corrections from that round are covered by the fixed price.',
        },
        {
          step: '05',
          title: 'Handover',
          text: 'You receive the live preview, the complete source code, access to the Git repository and basic documentation. Your team can continue building the same day.',
        },
      ],
    },
    configurator: {
      label: 'Scope & price',
      title: 'Configure your scope',
      highlight: 'Fixed Price',
      description: `Nine questions, one transparent price. The base scope starts at €${BASE_PRICE.toLocaleString('en-US')} and ${BASE_DAYS} business days. Every answer either adds nothing or a clearly stated amount plus additional business days. Nothing hidden, nothing subtracted. Payment runs in two instalments: 70% before the project starts, 30% before the data handover.`,
    },
    faq: {
      label: 'FAQ',
      title: 'Frequently asked',
      highlight: 'Questions',
      items: [
        {
          q: 'What exactly is a coded prototype and how is it different from a Figma prototype?',
          a: 'A Figma prototype links pictures of screens. A coded prototype is real software in the browser: input gets processed, states change, data is stored, error cases fire. That means it holds up when a user leaves the intended path, and that is exactly where the insight you are testing for appears.',
        },
        {
          q: 'Do I really get the source code?',
          a: 'Yes, in full and at no extra cost. Handover includes the complete source code, access to the Git repository with its entire history and basic documentation. There is no licence tie-in and no lock-in to CRE8NOW.',
        },
        {
          q: 'Can my engineering team keep building on the prototype?',
          a: 'That is what it is built for. CRE8NOW works with reusable components, a clear folder structure and common conventions so the prototype works as a starting point for product development. Anything deliberately simplified, such as mock data, is flagged in the documentation.',
        },
        {
          q: 'What if my project is bigger than the configurator covers?',
          a: 'Configure the closest scope and describe the rest in the form. The configurator covers the majority of typical prototype projects. For anything beyond it, CRE8NOW produces an individual fixed-price offer on the same logic after the scope call.',
        },
        {
          q: 'How binding are the price and the delivery date?',
          a: 'The numbers in the configurator are an initial estimate based on your answers. The scope call sharpens the detail, after which CRE8NOW confirms fixed price and delivery date in writing. From that point both are binding. Nothing is billed by the hour.',
        },
        {
          q: 'How does payment work?',
          a: 'In two instalments: 70% of the fixed price is due before the project starts, the remaining 30% before the data handover. The first instalment secures your slot in the calendar, the second only falls due once the prototype is finished and you have seen it. Source code, Git access and documentation transfer to you after that. There is no hourly billing and no follow-up invoicing as long as the scope stays unchanged.',
        },
        {
          q: 'Which technology stack is used?',
          a: 'The default is a modern React setup with Vite and Tailwind, extended where needed with a database, authentication and connections to external APIs or LLMs. If your team already works on a specific stack, CRE8NOW follows it wherever that makes sense.',
        },
        {
          q: 'How is data and privacy handled?',
          a: 'Prototypes run on mock data by default, real customer data is rarely needed for testing. Where persistence is required, CRE8NOW uses EU hosting and GDPR-compliant services. An NDA can be signed before the project starts on request.',
        },
        {
          q: 'What does CRE8NOW need from you to make seven business days work?',
          a: 'A clear idea of the question the prototype should answer, one point of contact with decision-making authority and, where they exist, access to brand, design system and the relevant interfaces. If something is missing, the concept surcharge in the configurator covers exactly that extra work.',
        },
      ],
    },
    related: {
      label: 'Read on',
      title: 'Related services',
      items: [
        { href: '/en/design/prototyping', title: 'Prototyping', desc: 'The service page on prototyping as a UX method: clickable wireframes, early user feedback, validated concepts.' },
        { href: '/en/design/ux-design', title: 'UX Design', desc: 'User-centred product conception, research and flows, the groundwork under every prototype that holds up.' },
        { href: '/en/design/development', title: 'Development', desc: 'When the prototype becomes a product: implementation in Webflow, Framer or custom code.' },
        { href: '/en/ai/custom-ai-development', title: 'Custom AI Development', desc: 'Bespoke AI solutions and LLM integrations that already work for real inside the prototype.' },
      ],
    },
    finalCta: {
      title: 'Ready for a prototype that clicks?',
      desc: 'Build your scope in nine questions and get price and delivery time instantly. No commitment, no sign-up.',
      primary: 'Configure your project',
    },
  },
};

/* -------------------------------------------------------------- component */

const RapidPrototyping = ({ theme, lang = 'de' }) => {
  const c = CONTENT[lang] || CONTENT.de;
  const color = theme === 'dark' ? '#8B5CF6' : '#5B21B6';
  const [wordIndex, setWordIndex] = useState(0);
  const widestWord = useMemo(
    () => c.hero.words.reduce((a, w) => (w.length > a.length ? w : a), ''),
    [c.hero.words]
  );
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % c.hero.words.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [c.hero.words.length]);

  /* JSON-LD: Service + Offer (the base scope is a real, priced offer) and a
     FAQPage built from the same array the FAQ section renders, so the markup
     can never drift from the visible copy. Scoped + removed on unmount so it
     does not leak onto the other tabs. */
  useEffect(() => {
    const path = lang === 'de' ? '/de/prototyping' : '/en/prototyping';
    const ld = [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'CRE8NOW', item: ORIGIN + '/' },
          { '@type': 'ListItem', position: 2, name: 'Rapid Prototyping', item: ORIGIN + path },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        '@id': ORIGIN + path + '#service',
        name: c.serviceName,
        serviceType: 'Rapid Prototyping',
        provider: { '@id': ORIGIN + '/#organization' },
        areaServed: 'Worldwide',
        availableLanguage: ['German', 'English'],
        url: ORIGIN + path,
        description: c.seoDescription,
        offers: {
          '@type': 'Offer',
          price: BASE_PRICE,
          priceCurrency: 'EUR',
          priceSpecification: {
            '@type': 'PriceSpecification',
            price: BASE_PRICE,
            priceCurrency: 'EUR',
            valueAddedTaxIncluded: false,
            description: lang === 'de' ? 'Basis-Scope, Festpreis' : 'Base scope, fixed price',
          },
          availability: 'https://schema.org/InStock',
          url: ORIGIN + path + '#configurator',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        '@id': ORIGIN + path + '#faq',
        mainEntity: c.faq.items.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ];
    const scripts = ld.map((obj) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo-page', 'prototyping');
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
      return s;
    });
    return () => scripts.forEach((s) => s.remove());
  }, [c, lang]);

  // Hero parallax, same curve as the other tab heroes.
  const { scrollY } = useScroll();
  const h4Y = useTransform(scrollY, [70, 570], [0, -150]);
  const h4Opacity = useTransform(scrollY, [70, 420], [1, 0]);
  const headlineY = useTransform(scrollY, [90, 590], [0, -120]);
  const headlineOpacity = useTransform(scrollY, [90, 440], [1, 0]);
  const descY = useTransform(scrollY, [110, 610], [0, -90]);
  const descOpacity = useTransform(scrollY, [110, 460], [1, 0]);
  const buttonY = useTransform(scrollY, [130, 630], [0, -60]);
  const buttonOpacity = useTransform(scrollY, [130, 480], [1, 0]);

  const staggerContainer = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };
  const fadeInUp = useMemo(
    () => ({
      hidden: { opacity: 0, y: typeof window !== 'undefined' && window.innerWidth < 768 ? 20 : 40 },
      visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
    }),
    []
  );

  const scrollToConfigurator = (location) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'prototype_configurator_open', { location });
    }
    const el = document.getElementById('configurator');
    if (el) smoothScrollIntoView(el, 90);
  };

  return (
    <div className="w-full">
      {/* ===================================================== HERO / 001 */}
      {/* Solid violet poster background, fixed regardless of the light/dark
          toggle — same poster identity as the Design/AI/Concierge heroes,
          white ink with yellow micrographic marks. */}
      <section
        className="relative min-h-[100dvh] w-full flex items-center justify-center gpu-accelerated hero-section-container"
        style={{ backgroundColor: 'var(--cre8-violet)' }}
      >
        <GridOverlay columns={12} opacity={0.08} color="#ffffff" />

        <div className="hidden md:block absolute inset-0 z-10 pointer-events-none" style={{ color: '#ffffff' }}>
          <CropMarks insetTop={8} insetX={8} insetBottom={8} color="var(--cre8-yellow)" />
          <div className="absolute" style={{ top: '38%', left: 24 }}>
            <Crosshair size={12} color="var(--cre8-yellow)" />
          </div>
          <div className="absolute bottom-20 left-6">
            <FlowChain steps={['IDEA', 'PROTOTYPE', 'CODE']} color="var(--cre8-yellow)" />
          </div>
          <div className="flex absolute bottom-20 right-6 items-center gap-3">
            <SystemMarker label="CRE8NOW / BUILD" color="var(--cre8-yellow)" />
            <SectionCounter current={1} total={TOTAL_SECTIONS} />
          </div>
          <VerticalLabel text="CRE8NOW / SYSTEM" side="left" offset={24} />
          <VerticalLabel text="MODE / PROTOTYPING" side="right" offset={24} />
        </div>

        <div
          className="hero-stack relative z-10 w-full px-6 md:px-[8vw] max-w-5xl mx-auto flex flex-col items-center text-center pointer-events-none"
          style={{ color: '#ffffff' }}
        >
          <Motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="flex flex-col items-center text-center pointer-events-auto"
          >
            <Motion.h4
              variants={fadeInUp}
              className="hero-h4 block mb-1 leading-[0.95] uppercase tracking-tight [text-shadow:none]"
              style={{ y: h4Y, opacity: h4Opacity }}
            >
              {c.hero.kicker}
            </Motion.h4>

            {/* Same rotating-H1 construction as the Concierge hero: the
                service keyword rides in the h4 above, the H1 cycles the goal
                the visitor actually came with. Invisible spacer holds the
                line height so the layout never jumps between words. */}
            <Motion.h1
              variants={fadeInUp}
              className="font-black leading-[0.85] tracking-tight uppercase w-full mt-4"
              style={{ y: headlineY, opacity: headlineOpacity }}
            >
              <Motion.span
                className="relative inline-block h-[1.1em] md:h-[1em] w-full align-top"
                style={{ perspective: '1000px' }}
              >
                <AnimatePresence mode="wait">
                  <Motion.span
                    key={wordIndex}
                    initial={{ scale: 1.2, opacity: 0, filter: 'blur(10px)' }}
                    animate={{
                      scale: 1,
                      opacity: 1,
                      filter: 'blur(0px)',
                      transition: { duration: 1.5, ease: [0.22, 1, 0.36, 1] },
                    }}
                    exit={{
                      scale: 0.8,
                      opacity: 0,
                      filter: 'blur(10px)',
                      transition: { duration: 1, ease: [0.64, 0, 0.78, 0] },
                    }}
                    className="absolute left-1/2 -translate-x-1/2 top-0 block origin-center whitespace-nowrap uppercase text-[1.08em] md:text-[1.12em] audiowide-regular font-normal"
                    style={{ color: 'var(--cre8-yellow)' }}
                  >
                    {c.hero.words[wordIndex]}
                  </Motion.span>
                </AnimatePresence>
                <span
                  className="invisible opacity-0 pointer-events-none whitespace-nowrap uppercase px-4 text-[1.08em] md:text-[1.12em] audiowide-regular font-normal"
                  aria-hidden="true"
                >
                  {widestWord}
                </span>
              </Motion.span>
            </Motion.h1>

            <Motion.p
              variants={fadeInUp}
              className="max-w-[1200px] w-[90vw] hero-copy mx-auto leading-relaxed font-light mb-8 mt-1 md:-mt-3 gpu-accelerated text-center"
              style={{ y: descY, opacity: descOpacity, color: 'rgba(255,255,255,0.8)' }}
            >
              {c.hero.description}
            </Motion.p>

            <Motion.div
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 }}
              className="w-full flex flex-wrap justify-center items-center gap-4"
              style={{ y: buttonY, opacity: buttonOpacity }}
            >
              <button
                onClick={() => scrollToConfigurator('hero')}
                style={{ backgroundColor: '#ffffff', color: 'var(--cre8-violet)' }}
                className="group relative px-[2.4em] py-[1.1em] font-black uppercase ui-text transition-all duration-500 overflow-hidden rounded-[4px]"
              >
                <div
                  className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500"
                  style={{ backgroundColor: 'var(--cre8-ink)' }}
                />
                <span className="relative z-10 flex items-center group-hover:text-white transition-colors duration-500">
                  {c.hero.cta}
                  <span className="w-0 opacity-0 group-hover:w-[1.25em] group-hover:ml-[0.75em] group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
                    <ArrowRight size="1.1em" />
                  </span>
                </span>
              </button>
              {/* Deliberately a single hero CTA: the scope call is what the
                  configurator hands off to, so offering it here competes with
                  the one action this page is built around. It stays available
                  in the sections further down (and in the nav). */}
            </Motion.div>
          </Motion.div>
        </div>

        {/* USP marquee along the hero floor — the shared HeroMarqueeTrack,
            which also paces the loop per item (marqueeDuration). */}
        <Motion.div
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          className="absolute bottom-0 left-0 right-0 hero-marquee-container z-10 w-full overflow-hidden border-t border-white/15"
        >
          <HeroMarqueeTrack items={c.hero.marquee} ink="#ffffff" accent="var(--cre8-yellow)" bg="var(--cre8-violet)" />
        </Motion.div>
      </section>

      {/* ============================================ PROOF STRIP (no index)
          The sitewide stats strip (components/Stats) — same component as the
          solution pages and case studies; only the accent follows the page. */}
      <StatsStrip items={c.proof} color={color} />

      {/* ======================================== TEASER VIDEO (same frame as
          the Concierge teaser).

          Gated on PROTOTYPING_VIDEO at the top of this file: set it to null
          to hide the section rather than show a play button with no reel. */}
      {PROTOTYPING_VIDEO && (
      <section className="py-16 md:py-24 bg-surface-color/5">
        <div className="max-w-[1800px] mx-auto px-6 md:px-20 flex flex-col items-center">
          <TeaserVideo src={PROTOTYPING_VIDEO} />
        </div>
      </section>
      )}

      {/* ==================================================== PAIN POINTS / 002 */}
      <section id="pains" className="relative py-20 md:py-32 bg-bg-color">
        <SectionFrame code="P" num="002" color={color} verticalLabelRight="MODE / FRICTION" />
        <div className="hidden md:block absolute bottom-6 right-6 z-10">
          <SectionCounter current={2} total={TOTAL_SECTIONS} color={color} />
        </div>
        <div className="max-w-[1800px] mx-auto px-6 md:px-20">
          <SectionHeading
            label={c.pains.label}
            title={c.pains.title}
            highlight={c.pains.highlight}
            description={c.pains.description}
            color={color}
            centered
          />
          <div className="mt-4">
            {c.pains.items.map((item, i) => (
              <LedgerRow key={item.title} item={item} idx={i} color={color} />
            ))}
          </div>
          <SectionCTA
            color={color}
            label={c.hero.cta}
            onClick={() => scrollToConfigurator('pains_bottom')}
          />
        </div>
      </section>

      {/* ========================================================= WINS / 003 */}
      <section id="wins" className="relative py-20 md:py-32 bg-surface-color/5">
        <SectionFrame code="P" num="003" color={color} verticalLabelRight="MODE / OUTCOME" />
        <div className="hidden md:block absolute bottom-6 right-6 z-10">
          <SectionCounter current={3} total={TOTAL_SECTIONS} color={color} />
        </div>
        <div className="max-w-[1800px] mx-auto px-6 md:px-20">
          <SectionHeading
            label={c.wins.label}
            title={c.wins.title}
            highlight={c.wins.highlight}
            description={c.wins.description}
            color={color}
            centered
          />
          <div className="mt-4">
            {c.wins.items.map((item, i) => (
              <LedgerRow key={item.title} item={item} idx={i} color={color} tone="win" />
            ))}
          </div>
          <SectionCTA
            color={color}
            label={c.hero.cta}
            onClick={() => scrollToConfigurator('wins_bottom')}
          />
        </div>
      </section>

      {/* ===================================================== CRE8APPS / 004 */}
      <section id="apps" className="relative py-20 md:py-32 bg-bg-color border-t border-border-color overflow-hidden">
        <SectionFrame code="P" num="004" color={color} verticalLabelRight="MODE / PRODUCTS" />
        <div className="hidden md:block absolute bottom-6 right-6 z-10">
          <SectionCounter current={4} total={TOTAL_SECTIONS} color={color} />
        </div>
        <Cre8AppsGallery color={color} lang={lang} />
      </section>

      {/* ============================================== GOALS / AUDIENCE / 005 */}
      <section id="goals" className="relative py-20 md:py-32 overflow-hidden bg-bg-color">
        <SectionFrame code="P" num="005" color={color} verticalLabelRight="MODE / GOALS" />
        <div className="hidden md:block absolute bottom-6 right-6 z-10">
          <SectionCounter current={5} total={TOTAL_SECTIONS} color={color} />
        </div>
        <div className="w-full">
          <div className="max-w-[1800px] mx-auto px-6 md:px-20">
            <SectionHeading
              label={c.goals.label}
              title={c.goals.title}
              highlight={c.goals.highlight}
              description={c.goals.description}
              color={color}
              centered
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-border-color mt-12 md:mt-16 w-full">
            {c.goals.items.map((item, i) => (
              <GoalCard key={item.title} item={item} i={i} color={color} theme={theme} />
            ))}
          </div>
          <div className="max-w-[1800px] mx-auto px-6 md:px-20">
            <SectionCTA
              color={color}
              label={c.hero.cta}
              onClick={() => scrollToConfigurator('goals_bottom')}
            />
          </div>
        </div>
      </section>

      {/* ====================================================== PROCESS / 006 */}
      <section id="process" data-gsap-section className="relative bg-bg-color pt-20 pb-20 md:pt-32 md:pb-24">
        <SectionFrame code="P" num="006" color={color} verticalLabelRight="MODE / PROCESS" />
        <div className="max-w-[1800px] mx-auto px-6 md:px-20">
          <SectionHeading
            label={c.process.label}
            title={c.process.title}
            highlight={c.process.highlight}
            description={c.process.description}
            color={color}
            centered
          />
        </div>
      </section>

      {/* The main page's pinned, scroll-driven step track, same component and
          same step shape ({ step, title, text }). */}
      <ProcessPinnedSection
        key={`prototyping-${lang}`}
        steps={c.process.steps}
        color={color}
        t={{ common: { step: lang === 'de' ? 'Schritt' : 'Step' } }}
      />

      <section className="relative bg-bg-color">
        <div className="max-w-[1800px] mx-auto px-6 md:px-20">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 pb-4">
            <span className="font-mono-swiss text-[10px] uppercase tracking-[0.3em] text-text-muted">
              {lang === 'de' ? 'Immer dabei' : 'Always included'}
            </span>
            {(lang === 'de'
              ? ['Vollständiger Quellcode', 'Git-Repository', 'Live-Preview', 'Responsive UI', 'QA & Übergabe', 'Basis-Dokumentation']
              : ['Complete source code', 'Git repository', 'Live preview', 'Responsive UI', 'QA & handover', 'Basic documentation']
            ).map((d) => (
              <span key={d} className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.15em]">
                <Check size={12} strokeWidth={3} style={{ color }} />
                {d}
              </span>
            ))}
          </div>
          <SectionCTA
            color={color}
            label={c.hero.cta}
            onClick={() => scrollToConfigurator('process_bottom')}
          />
        </div>
      </section>

      {/* ================================================= CONFIGURATOR / 007 */}
      <section id="configurator" className="relative py-20 md:py-32 bg-bg-color border-t border-border-color">
        <SectionFrame code="P" num="007" color={color} verticalLabelRight="MODE / SCOPE" />
        <div className="hidden md:block absolute bottom-6 right-6 z-10">
          <SectionCounter current={7} total={TOTAL_SECTIONS} color={color} />
        </div>
        <div className="max-w-[1800px] mx-auto px-6 md:px-20">
          <SectionHeading
            label={c.configurator.label}
            title={c.configurator.title}
            highlight={c.configurator.highlight}
            description={c.configurator.description}
            color={color}
            centered
          />
          <PrototypeConfigurator color={color} lang={lang} />
        </div>
      </section>

      {/* ========================================================== FAQ / 008 */}
      <section id="faq" className="relative py-20 md:py-32 border-t border-border-color bg-surface-color/5">
        <SectionFrame code="P" num="008" color={color} verticalLabelRight="MODE / FAQ" />
        <div className="hidden md:block absolute bottom-6 right-6 z-10">
          <SectionCounter current={8} total={TOTAL_SECTIONS} color={color} />
        </div>
        <div className="w-full px-[8vw]">
          <SectionHeading label={c.faq.label} title={c.faq.title} highlight={c.faq.highlight} color={color} centered />
          <div className="mt-16">
            {c.faq.items.map((item, i) => (
              <FaqItem key={item.q} item={item} idx={i} color={color} />
            ))}
          </div>
        </div>
      </section>

      {/* =================================================== RELATED LINKS */}
      <section className="relative py-16 md:py-24 border-t border-border-color bg-bg-color">
        <div className="max-w-[1800px] mx-auto px-6 md:px-20">
          <div className="flex items-center gap-3 mb-10">
            <span className="w-[5px] h-[5px] flex-shrink-0" style={{ backgroundColor: color }} />
            <span className="font-mono-swiss text-[10px] tracking-[0.3em] uppercase text-text-muted">
              {c.related.label}
            </span>
            <div className="h-px flex-1 opacity-30" style={{ backgroundColor: color }} />
            <span className="font-mono-swiss text-[10px] tracking-[0.3em] uppercase text-text-muted">
              {c.related.title}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-border-color">
            {c.related.items.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="group p-8 border-b border-r border-border-color transition-colors duration-500 hover:bg-surface-color/40"
              >
                <h4 className="text-base font-black tracking-tight mb-3 flex items-center gap-2">
                  {item.title}
                  <ArrowRight
                    size={14}
                    className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                    style={{ color }}
                  />
                </h4>
                <p className="text-xs text-text-muted font-light leading-relaxed">{item.desc}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* The closing CTA that used to sit here is gone: the configurator above
          is the page's single action, and repeating it under a second headline
          only pushed the footer further away. Its copy stays in CONTENT.finalCta
          for both languages in case it comes back. */}
    </div>
  );
};

export default RapidPrototyping;
